// ---------------------------------------------------------------------------
// 部門別 予算・実績対比表 データ取得・整形 (2026-06-12)
//   実績/前年 = freee trial_pl(section_id 付き・worker /api/freee-proxy 経由・GET読取のみ)。
//   予算 = D1 budget_monthly(/api/budget・freee に予算は無い)。
//   集計は budgetActualLayout(la_soffitta 完全踏襲)+ freee 決算書カテゴリー集計 + 派生式。
//   既存 FinancialStatementsPage / reports.ts のデータ源・ロバスト方針(新規勘定の自動挿入・黙殺禁止)を踏襲。
// ---------------------------------------------------------------------------

import { fiscalMonthToCalendar, lastDayOfMonth } from '../types/reports';
import { adjustmentQuery } from './adjustmentMode';
import {
  BUDGET_LAYOUT, BudgetLayoutRow, DiffType,
  LABOR_LABELS, SELLING_LABELS, SALES_LABELS, COGS_TERMS,
  NONOP_INCOME_LABELS, NONOP_EXPENSE_LABELS, FREEE_GROUP, CATEGORY_TO_SUBTOTAL,
} from '../data/budgetActualLayout';

const API_BASE = 'https://invoice-review.system-0f7.workers.dev';
const COMPANY_ID = 11518223;

export interface BudgetActualCellRow {
  excelRow: number;
  label: string;
  kind: 'detail' | 'subtotal';
  catChar?: string;
  indent: number;
  isSubtotal: boolean;
  isNew?: boolean;            // 新規(テンプレ外)freee 勘定科目=自動挿入
  prev: number; prevRatio: number;
  budget: number; budgetRatio: number;
  actual: number; actualRatio: number;
  vsPrev: number | null;     // 対前年差(diffType により 金額/達成率/構成比pt)
  vsBudget: number | null;   // 対予算差
  diffType: DiffType;
}

export interface BudgetActualPanel {
  title: string;
  periodLabel: string;       // 「2026-03-01 ～ 2026-03-31」
  rows: BudgetActualCellRow[];
}

export interface BudgetUnmapped { accountName: string; categoryName: string; amount: number; }

export interface BudgetActualResult {
  storeName: string;
  sectionId: number;
  fiscalTerm: number;
  printedAt: string;
  single: BudgetActualPanel;
  cumulative: BudgetActualPanel;
  meta: { empty: boolean; budgetMissing: boolean; unmapped: BudgetUnmapped[] };
}

export interface TrialData {
  byName: Map<string, number>;      // account_item_name -> 発生(closing-opening)
  catByName: Map<string, string>;   // account_item_name -> account_category_name
  groupTotals: Map<string, number>; // account_category_name(total_line) -> 発生
  debitByName: Map<string, number>; // account_item_name -> 借方金額(gross。社員割引の振替前グロス取得用)
}

export interface Range { start: string; end: string; }

function ymd(y: number, m: number): string {
  return `${y}-${String(m).padStart(2, '0')}-01`;
}

export async function fetchTrial(disp: 'account_item' | 'group', range: Range, sectionId: number): Promise<TrialData> {
  const q = new URLSearchParams({
    company_id: String(COMPANY_ID),
    start_date: range.start, end_date: range.end,
    account_item_display_type: disp,
    ...adjustmentQuery(), // 整理仕訳 含む/除く (adjustmentMode.ts)
  });
  if (sectionId > 0) q.set('section_id', String(sectionId));
  const url = `${API_BASE}/api/freee-proxy/api/1/reports/trial_pl?${q.toString()}`;
  // 429(レート制限)/503 は指数バックオフでリトライ(山城屋合算など複数section同時取得時の対策)。
  let res: Response | undefined;
  for (let attempt = 0; attempt < 4; attempt++) {
    res = await fetch(url);
    if (res.ok) break;
    if ((res.status === 429 || res.status === 503) && attempt < 3) {
      await new Promise((r) => setTimeout(r, 700 * (attempt + 1)));
      continue;
    }
    break;
  }
  if (!res || !res.ok) {
    let body: any = null; try { body = res ? await res.json() : null; } catch { /* */ }
    throw new Error(`freee trial_pl(${disp}) failed: ${res ? res.status : 'no-response'} ${body?.message || ''}`);
  }
  const j = await res.json();
  const balances: any[] = (j?.trial_pl?.balances) || [];
  const byName = new Map<string, number>();
  const catByName = new Map<string, string>();
  const groupTotals = new Map<string, number>();
  const debitByName = new Map<string, number>();
  for (const b of balances) {
    const op = b.opening_balance ?? 0;
    const cl = b.closing_balance ?? 0;
    const hatsu = cl - op;
    if (b.total_line) {
      const cat = b.account_category_name;
      if (cat && !groupTotals.has(cat)) groupTotals.set(cat, hatsu);
    } else if (b.account_item_name) {
      const nm = b.account_item_name as string;
      byName.set(nm, (byName.get(nm) || 0) + hatsu);
      debitByName.set(nm, (debitByName.get(nm) || 0) + (b.debit_amount ?? 0));
      if (b.account_category_name) catByName.set(nm, b.account_category_name);
    }
  }
  return { byName, catByName, groupTotals, debitByName };
}

/** 複数 section の TrialData を合算(山城屋=庄藏+分店 等の仮想合算部門用)。 */
function mergeTrials(ts: TrialData[]): TrialData {
  const byName = new Map<string, number>();
  const catByName = new Map<string, string>();
  const groupTotals = new Map<string, number>();
  const debitByName = new Map<string, number>();
  for (const t of ts) {
    t.byName.forEach((v, k) => byName.set(k, (byName.get(k) || 0) + v));
    t.catByName.forEach((v, k) => { if (!catByName.has(k)) catByName.set(k, v); });
    t.groupTotals.forEach((v, k) => groupTotals.set(k, (groupTotals.get(k) || 0) + v));
    t.debitByName.forEach((v, k) => debitByName.set(k, (debitByName.get(k) || 0) + v));
  }
  return { byName, catByName, groupTotals, debitByName };
}

/** combine 指定時は複数 section を取得して合算、 未指定なら単一 section。 */
async function fetchTrialMaybe(disp: 'account_item' | 'group', range: Range, sectionId: number, combine?: number[]): Promise<TrialData> {
  if (combine && combine.length) {
    // 直列取得(429回避: 並列バーストを単一店と同水準に抑える)。
    const parts: TrialData[] = [];
    for (const sid of combine) parts.push(await fetchTrial(disp, range, sid));
    return mergeTrials(parts);
  }
  return fetchTrial(disp, range, sectionId);
}

/** combine 指定時は複数 section の予算を display_item×fiscal_month で合算。 */
async function fetchBudgetRows(fiscalTerm: number, sectionId: number, combine?: number[]): Promise<{ rows: any[] }> {
  const get = (sid: number) => fetch(`${API_BASE}/api/budget?fiscal_term=${fiscalTerm}&section_id=${sid}`).then((r) => r.ok ? r.json() : { rows: [] });
  if (!combine || !combine.length) return get(sectionId);
  const all = await Promise.all(combine.map(get));
  const m = new Map<string, { display_item: string; fiscal_month: number; amount: number }>();
  for (const j of all) for (const r of (j.rows || [])) {
    const k = `${r.display_item}@${r.fiscal_month}`;
    const e = m.get(k) || { display_item: r.display_item, fiscal_month: Number(r.fiscal_month), amount: 0 };
    e.amount += Number(r.amount) || 0; m.set(k, e);
  }
  return { rows: Array.from(m.values()) };
}

const round2 = (v: number) => Math.round(v * 100) / 100;
const ratioOf = (v: number, denom: number) => (denom ? round2((v / denom) * 100) : 0);

type Triple = { prev: number; budget: number; actual: number };
const T0 = (): Triple => ({ prev: 0, budget: 0, actual: 0 });
const add = (a: Triple, b: Triple, sign = 1): Triple => ({
  prev: a.prev + sign * b.prev, budget: a.budget + sign * b.budget, actual: a.actual + sign * b.actual,
});

/** 1パネル(単月 or 累計)の全行を組成 */
function buildPanel(
  title: string, periodLabel: string,
  actualAI: TrialData, actualG: TrialData,
  prevAI: TrialData, prevG: TrialData,
  budgetByItem: Map<string, number>,   // display_item -> その期間の予算合計
  unmappedSink: Map<string, BudgetUnmapped>,
  isCumulative: boolean,
): BudgetActualPanel {
  const resolved = new Map<string, Triple>();
  const detailVal = (row: BudgetLayoutRow): Triple => {
    if (row.grossDebit && row.freeeName) {
      // 社員割引: net(月末に福利厚生費へ振替され0)でなく借方グロスを actual/前年 に採用。
      return {
        prev: prevAI.debitByName.get(row.freeeName) || 0,
        budget: row.budgetItem ? (budgetByItem.get(row.budgetItem) || 0) : 0,
        actual: actualAI.debitByName.get(row.freeeName) || 0,
      };
    }
    if (row.freeeName) {
      return {
        prev: prevAI.byName.get(row.freeeName) || 0,
        budget: row.budgetItem ? (budgetByItem.get(row.budgetItem) || 0) : 0,
        actual: actualAI.byName.get(row.freeeName) || 0,
      };
    }
    if (row.freeeCategory) {
      return {
        prev: prevG.groupTotals.get(row.freeeCategory) || 0,
        budget: 0,
        actual: actualG.groupTotals.get(row.freeeCategory) || 0,
      };
    }
    // freee leaf 非対応(本部売上/退職金/本部経費 等)。予算があれば予算のみ。
    return { prev: 0, budget: row.budgetItem ? (budgetByItem.get(row.budgetItem) || 0) : 0, actual: 0 };
  };

  // 1) detail を先に確定
  for (const row of BUDGET_LAYOUT) {
    if (row.kind !== 'detail') continue;
    resolved.set(row.label, detailVal(row));
  }
  const sumLabels = (labels: string[]): Triple =>
    labels.reduce((a, l) => add(a, resolved.get(l) || T0()), T0());

  // 2) container subtotal(順序依存。labor → sga → selling)
  const netSales: Triple = {
    prev: prevG.groupTotals.get(FREEE_GROUP.netSales) || 0,
    budget: SALES_LABELS.reduce((s, l) => s + (resolved.get(l)?.budget || 0), 0),
    actual: actualG.groupTotals.get(FREEE_GROUP.netSales) || 0,
  };
  const cogs: Triple = {
    prev: prevG.groupTotals.get(FREEE_GROUP.cogs) || 0,
    budget: COGS_TERMS.reduce((s, t) => s + t.sign * (resolved.get(t.label)?.budget || 0), 0),
    actual: actualG.groupTotals.get(FREEE_GROUP.cogs) || 0,
  };
  const grossProfit = add(netSales, cogs, -1);
  const labor = sumLabels(LABOR_LABELS);
  // 家賃は 部門別予実では「市場家賃」で表示・集計(user指示 2026-06-17)。
  //   freee 販売管理費グループ合計は 地代家賃(実)を含み、市場家賃(借+)と市場家賃振替(貸-)が相殺(ネット0)。
  //   → 合計行も市場家賃に揃えるため、グループ合計から 地代家賃 を除き 市場家賃 を加算。
  const mktRentAdj = (ai: TrialData): number => (ai.byName.get('市場家賃') || 0) - (ai.byName.get('地代家賃') || 0);
  const sga: Triple = {
    prev: (prevG.groupTotals.get(FREEE_GROUP.sga) || 0) + mktRentAdj(prevAI),
    budget: labor.budget + SELLING_LABELS.reduce((s, l) => s + (resolved.get(l)?.budget || 0), 0),
    actual: (actualG.groupTotals.get(FREEE_GROUP.sga) || 0) + mktRentAdj(actualAI),
  };
  const selling = add(sga, labor, -1);
  const operating = add(grossProfit, sga, -1);
  const nonopIncome: Triple = {
    prev: prevG.groupTotals.get(FREEE_GROUP.nonopIncome) || 0,
    budget: NONOP_INCOME_LABELS.reduce((s, l) => s + (resolved.get(l)?.budget || 0), 0),
    actual: actualG.groupTotals.get(FREEE_GROUP.nonopIncome) || 0,
  };
  const nonopExpense: Triple = {
    prev: prevG.groupTotals.get(FREEE_GROUP.nonopExpense) || 0,
    budget: NONOP_EXPENSE_LABELS.reduce((s, l) => s + (resolved.get(l)?.budget || 0), 0),
    actual: actualG.groupTotals.get(FREEE_GROUP.nonopExpense) || 0,
  };
  const ordinary = add(add(operating, nonopIncome), nonopExpense, -1);
  const extraIncome = resolved.get('特別利益') || T0();
  const extraLoss = resolved.get('特別損失') || T0();
  const pretax = add(add(ordinary, extraIncome), extraLoss, -1);
  const honbu = resolved.get('本部経費') || T0();
  const storeOrdinary = add(ordinary, honbu, -1);

  const subtotalValues: Record<string, Triple> = {
    net_sales: netSales, cogs, gross_profit: grossProfit, labor, selling, sga,
    operating, nonop_income: nonopIncome, nonop_expense: nonopExpense,
    ordinary, pretax, store_ordinary: storeOrdinary,
  };
  for (const row of BUDGET_LAYOUT) {
    if (row.kind === 'subtotal' && row.subtotalKind) resolved.set(row.label, subtotalValues[row.subtotalKind]);
  }

  // 3) 未マッピング(新規)freee 勘定科目 → 所属 subtotal 直前に自動挿入(表示専用・subtotal は group 値で既に内包)
  const consumed = new Set<string>();
  for (const row of BUDGET_LAYOUT) if (row.freeeName) consumed.add(row.freeeName);
  // 家賃=市場家賃 表示のため、地代家賃・市場家賃振替 は新規勘定として別表示しない(市場家賃へ集約済)。
  consumed.add('地代家賃'); consumed.add('市場家賃振替');
  const newByTarget = new Map<string, { name: string; prev: number; actual: number }[]>();
  actualAI.byName.forEach((actualHatsu, name) => {
    if (consumed.has(name)) return;
    const prevHatsu = prevAI.byName.get(name) || 0;
    if (actualHatsu === 0 && prevHatsu === 0) return;
    const cat = actualAI.catByName.get(name) || prevAI.catByName.get(name) || '';
    const hit = CATEGORY_TO_SUBTOTAL.find((c) => cat.includes(c.match));
    const target = hit ? hit.subtotal : '販売経費'; // 不明は販売経費(その他販管費)へ寄せる
    if (!newByTarget.has(target)) newByTarget.set(target, []);
    newByTarget.get(target)!.push({ name, prev: prevHatsu, actual: actualHatsu });
    if (!unmappedSink.has(name)) {
      unmappedSink.set(name, { accountName: name, categoryName: cat || '(カテゴリー不明)', amount: actualHatsu });
    }
  });

  // 4) 構成比分母 = 純売上高
  const out: BudgetActualCellRow[] = [];
  const mkRow = (
    label: string, v: Triple, opts: { excelRow: number; kind: 'detail' | 'subtotal'; catChar?: string; indent: number; diffType: DiffType; isNew?: boolean },
  ): BudgetActualCellRow => {
    const prevRatio = ratioOf(v.prev, netSales.prev);
    const budgetRatio = ratioOf(v.budget, netSales.budget);
    const actualRatio = ratioOf(v.actual, netSales.actual);
    let vsPrev: number | null;
    let vsBudget: number | null;
    if (opts.diffType === 'achievement') {
      vsPrev = v.prev ? round2((v.actual / v.prev) * 100) : 0;
      vsBudget = v.budget ? round2((v.actual / v.budget) * 100) : 0;
    } else if (opts.diffType === 'ratio_point') {
      vsPrev = round2(actualRatio - prevRatio);
      vsBudget = round2(actualRatio - budgetRatio);
    } else {
      vsPrev = v.actual - v.prev;
      vsBudget = v.actual - v.budget;
    }
    return {
      excelRow: opts.excelRow, label, kind: opts.kind, catChar: opts.catChar, indent: opts.indent,
      isSubtotal: opts.kind === 'subtotal', isNew: opts.isNew,
      prev: v.prev, prevRatio, budget: v.budget, budgetRatio, actual: v.actual, actualRatio,
      vsPrev, vsBudget, diffType: opts.diffType,
    };
  };

  for (const row of BUDGET_LAYOUT) {
    // 社員割引(grossDebit)は単月・累計とも「営業収益の最下部に常に表示」。
    //   小計(純売上高/一般管理費 等)には一切加算しない情報行なので、累計でも福利厚生費と二重計上にはならない。
    //   累計では当該期間の累計グロス(借方合計)を表示する。
    // subtotal の直前に、その group へ寄せた新規科目を挿入
    if (row.kind === 'subtotal') {
      const extra = newByTarget.get(row.label);
      if (extra) {
        for (const it of extra) {
          out.push(mkRow(it.name, { prev: it.prev, budget: 0, actual: it.actual },
            { excelRow: row.excelRow, kind: 'detail', indent: 1, diffType: 'amount', isNew: true }));
        }
      }
    }
    const v = resolved.get(row.label) || T0();
    out.push(mkRow(row.label, v, {
      excelRow: row.excelRow, kind: row.kind, catChar: row.catChar, indent: row.indent, diffType: row.diffType,
    }));
  }
  return { title, periodLabel, rows: out };
}

function printedNow(): string {
  // 原本PDF J1 と同じ YYYY/MM/DD 形式(時刻なし)
  const n = new Date();
  const p = (x: number) => String(x).padStart(2, '0');
  return `${n.getFullYear()}/${p(n.getMonth() + 1)}/${p(n.getDate())}`;
}

/**
 * 部門別 予算・実績対比表を取得・組成。
 * @param fiscalTerm 会計期(80 等)
 * @param fiscalMonth 対象月(1=10月 … 12=9月)。単月パネル=この月、累計パネル=期首〜この月。
 * @param sectionId freee 部門ID(0=全店舗合算)
 * @param storeName 表示用 店舗名
 */
export async function fetchBudgetActual(
  fiscalTerm: number, fiscalMonth: number, sectionId: number, storeName: string, combine?: number[],
): Promise<BudgetActualResult> {
  // 期間解決(会計月 1=10月)。半期: 上期=fm1-6(10月始) / 下期=fm7-12(4月始)。
  //   累計(裏面 半期合計)は当該半期の起点から積み上げ → 上期は10/1〜、下期は4/1〜で自動切替。
  const halfStartFm = fiscalMonth <= 6 ? 1 : 7;
  const cMon = fiscalMonthToCalendar(fiscalTerm, fiscalMonth);
  const cFirst = fiscalMonthToCalendar(fiscalTerm, halfStartFm);
  const pMon = fiscalMonthToCalendar(fiscalTerm - 1, fiscalMonth);
  const pFirst = fiscalMonthToCalendar(fiscalTerm - 1, halfStartFm);
  const single: Range = { start: ymd(cMon.year, cMon.month), end: lastDayOfMonth(cMon.year, cMon.month) };
  const ytd: Range = { start: ymd(cFirst.year, cFirst.month), end: lastDayOfMonth(cMon.year, cMon.month) };
  const prevSingle: Range = { start: ymd(pMon.year, pMon.month), end: lastDayOfMonth(pMon.year, pMon.month) };
  const prevYtd: Range = { start: ymd(pFirst.year, pFirst.month), end: lastDayOfMonth(pMon.year, pMon.month) };

  const [
    sAI, sG, sPrevAI, sPrevG,   // 単月: 実績(AI/G), 前年(AI/G)
    cAI, cG, cPrevAI, cPrevG,   // 累計: 実績(AI/G), 前年(AI/G)
    budgetResp,
  ] = await Promise.all([
    fetchTrialMaybe('account_item', single, sectionId, combine),
    fetchTrialMaybe('group', single, sectionId, combine),
    fetchTrialMaybe('account_item', prevSingle, sectionId, combine),
    fetchTrialMaybe('group', prevSingle, sectionId, combine),
    fetchTrialMaybe('account_item', ytd, sectionId, combine),
    fetchTrialMaybe('group', ytd, sectionId, combine),
    fetchTrialMaybe('account_item', prevYtd, sectionId, combine),
    fetchTrialMaybe('group', prevYtd, sectionId, combine),
    fetchBudgetRows(fiscalTerm, sectionId, combine),
  ]);

  // 予算: display_item -> {fiscal_month -> amount}
  const budgetRows: { display_item: string; fiscal_month: number; amount: number }[] = budgetResp?.rows || [];
  const budgetMissing = budgetRows.length === 0;
  const singleBudget = new Map<string, number>();
  const cumBudget = new Map<string, number>();
  for (const r of budgetRows) {
    if (r.fiscal_month === fiscalMonth) singleBudget.set(r.display_item, (singleBudget.get(r.display_item) || 0) + r.amount);
    if (r.fiscal_month >= halfStartFm && r.fiscal_month <= fiscalMonth) cumBudget.set(r.display_item, (cumBudget.get(r.display_item) || 0) + r.amount);
  }

  const unmapped = new Map<string, BudgetUnmapped>();
  const half = fiscalMonth <= 6 ? '上期' : '下期';
  const title = `${fiscalTerm}期${half}損益 予算・実績対比書`;
  const singlePanel = buildPanel(
    title, `${single.start} ～ ${single.end}`,
    sAI, sG, sPrevAI, sPrevG, singleBudget, unmapped, false,
  );
  const cumPanel = buildPanel(
    `${title}（累計）`, `${ytd.start} ～ ${ytd.end}`,
    cAI, cG, cPrevAI, cPrevG, cumBudget, unmapped, true,
  );

  const empty = sAI.byName.size === 0 && cAI.byName.size === 0;
  return {
    storeName, sectionId, fiscalTerm, printedAt: printedNow(),
    single: singlePanel, cumulative: cumPanel,
    meta: { empty, budgetMissing, unmapped: Array.from(unmapped.values()) },
  };
}
